import { useNavigate, Link } from "react-router-dom";
import { useSpark } from "@/lib/store";
import { useEffect } from "react";
import { BrandHeader } from "@/components/BrandHeader";
import { PublicFooter } from "@/components/PublicFooter";

const HERO = "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=2000&q=75&auto=format&fit=crop";

const MODES = [
  { label: "Reflect", title: "Think it through", copy: "Guided conversations that help you say what's actually going on — in your own words, at your own pace.",
    photo: "https://images.unsplash.com/photo-1455390582262-044cdead277a?w=700&q=70&auto=format&fit=crop", alt: "A hand writing in a notebook" },
  { label: "Notice", title: "See the evidence", copy: "Spark gathers real examples from your working life and proposes the patterns — strengths, energy, values — for you to confirm or correct.",
    photo: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=700&q=70&auto=format&fit=crop", alt: "A laptop, notepad and coffee on a desk by a window" },
  { label: "Act", title: "Make your move", copy: "Turn a clear Starting Point and Career Compass into practical steps: your story, your value, your next conversation.",
    photo: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=700&q=70&auto=format&fit=crop", alt: "People working together at a table" },
];

const QUESTIONS = ["Why am I here?", "Who am I now?", "What do I want next?", "How am I going to make it happen?"];

export default function Welcome() {
  const { switchToSarah, startFresh, session, isPilot } = useSpark();
  const nav = useNavigate();
  const params = new URLSearchParams(window.location.search);
  if (params.get("dev") === "1") localStorage.setItem("spark_dev", "1");
  const isDev = localStorage.getItem("spark_dev") === "1";

  useEffect(() => { document.title = "Spark Coaching — Know what you want from your career. Then make it happen."; }, []);
  useEffect(() => { if (isPilot) nav("/dashboard", { replace: true }); }, [isPilot, nav]);

  const chooseSarah = () => { switchToSarah(); nav("/dashboard"); };
  const chooseFresh = async () => { await startFresh(); nav("/onboarding"); };
  if (session === undefined) return <div className="min-h-screen bg-white" />;

  return (
    <div className="min-h-screen bg-white">
      <BrandHeader links={[{ label: "How it works", href: "#how" }, { label: "The questions", href: "#questions" }, { label: "Privacy", to: "/welcome-note?review=1" }]} />

      {/* Hero — full-bleed brand photograph, dark scrim, bold white headline */}
      <section data-testid="welcome-hero" className="relative min-h-[82vh] flex items-center overflow-hidden bg-black">
        <img src={HERO} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-[55%_45%] spark-photo" loading="eager" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.40) 60%, rgba(0,0,0,0.55) 100%)" }} />
        <div className="relative spark-container w-full py-24 sm:py-32 text-center">
          <img src="/brand/monogram_white.png" alt="" aria-hidden="true" className="mx-auto h-20 w-20 sm:h-24 sm:w-24 spark-reveal" />
          <p className="spark-eyebrow mt-8 !text-white/80 spark-reveal" style={{ animationDelay: "0.1s" }}>Self-guided career coaching</p>
          <h1 className="spark-display mx-auto mt-5 max-w-4xl text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight !text-white spark-reveal" style={{ animationDelay: "0.2s" }}>
            Know what you want from your career. Then make it happen.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base sm:text-lg text-white/85 leading-relaxed spark-reveal" style={{ animationDelay: "0.3s" }}>
            Guided, self-paced career coaching for people who feel stuck, are questioning their direction, or are ready for a change.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3 spark-reveal" style={{ animationDelay: "0.4s" }}>
            <Link data-testid="welcome-next-step-hero-btn" to="/start" className="spark-btn-light">Find my next step</Link>
            <a href="#how" data-testid="welcome-how-link" className="spark-btn-outline-light !py-3 !px-7">How Spark works</a>
            {isDev && (
              <>
                <button data-testid="welcome-fresh-start-btn" onClick={chooseFresh} className="spark-btn-outline-light !py-3 !px-7">Begin — I'm new here (dev)</button>
                <button data-testid="welcome-sarah-demo-btn" onClick={chooseSarah} className="spark-btn-outline-light !py-3 !px-7">Explore as Sarah (demo)</button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Experience — bold heading, short black bar, centred copy */}
      <section className="spark-container py-24 sm:py-32 text-center">
        <h2 data-testid="welcome-experience-heading" className="mx-auto max-w-3xl text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
          Get clearer on what you want from your career and what to do next.
        </h2>
        <div className="spark-bar mx-auto mt-7" />
        <p data-testid="welcome-experience-copy" className="mx-auto mt-8 max-w-2xl text-base sm:text-lg leading-relaxed" style={{ color: "hsl(var(--spark-ink-soft))" }}>
          Feeling stuck, questioning your direction or ready for a change? Spark helps you make sense of your strengths, understand what's holding you back and explore what matters to you so you can choose a practical next step.
        </p>
        <p data-testid="welcome-experience-copy-2" className="mx-auto mt-5 max-w-2xl text-base sm:text-lg leading-relaxed" style={{ color: "hsl(var(--spark-ink-soft))" }}>
          A guided, self-paced experience built on evidence-based career coaching and the expertise of a Master's-qualified coach.
        </p>
        <Link to="/start" data-testid="welcome-next-step-btn" className="spark-btn-primary mt-10">Find my next step</Link>
      </section>

      {/* How it works — three circular photographs */}
      <section id="how" data-testid="welcome-how-section" className="py-24 sm:py-28" style={{ background: "hsl(var(--spark-cool))" }}>
        <div className="spark-container">
          <div className="text-center">
            <p className="spark-eyebrow-copper">How Spark works</p>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Reflect. Notice. Act.</h2>
            <div className="spark-bar mx-auto mt-7" />
          </div>
          <div className="mt-16 grid gap-14 sm:grid-cols-3 sm:gap-10">
            {MODES.map((m, i) => (
              <article key={m.label} data-testid={`welcome-mode-${m.label.toLowerCase()}`} className="text-center">
                <div className="mx-auto h-48 w-48 sm:h-44 sm:w-44 lg:h-56 lg:w-56 rounded-full overflow-hidden ring-1 ring-black/10 bg-white">
                  <img src={m.photo} alt={m.alt} loading="lazy" className="spark-photo h-full w-full object-cover" />
                </div>
                <p className="spark-eyebrow mt-8">{String(i + 1).padStart(2, "0")} · {m.label}</p>
                <h3 className="mt-2 text-xl sm:text-2xl font-semibold tracking-tight">{m.title}</h3>
                <p className="mx-auto mt-3 max-w-xs text-sm sm:text-base leading-relaxed" style={{ color: "hsl(var(--spark-ink-soft))" }}>{m.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* The four questions — the only place gold appears: the Gold Thread between them */}
      <section id="questions" data-testid="welcome-questions-section" className="spark-container py-24 sm:py-32">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <p className="spark-eyebrow-copper">Spark helps you answer</p>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Four questions. One Gold Thread.</h2>
            <p className="mt-6 text-base leading-relaxed" style={{ color: "hsl(var(--spark-ink-soft))" }}>
              Part One finds your direction. Part Two makes your move. Every confirmed insight joins a single thread that runs through your Career Compass.
            </p>
            <Link to="/start" data-testid="welcome-questions-start-btn" className="spark-btn-primary mt-8">Find my next step — A$99</Link>
          </div>
          <ol className="lg:col-span-8 relative">
            <span aria-hidden="true" className="spark-thread-vertical absolute left-[11px] top-3 bottom-3 hidden sm:block" />
            {QUESTIONS.map((q, i) => (
              <li key={q} data-testid={`welcome-question-${i + 1}`} className="relative flex items-baseline gap-6 py-6 border-t border-black/10 first:border-t-0">
                <span className="relative z-10 hidden sm:flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-semibold ring-1 ring-black/15 spark-display">{i + 1}</span>
                <span className="spark-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight">{q}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
